import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { HistoryService } from './history.service';
import { catchError, EMPTY, filter, Observable, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import {
  BadgeComponent,
  CardContainerComponent,
  EnumBadgeColor,
  LoaderComponent
} from '@tribeauto/ngx-mobilite-material';
import {
  HistoryElement,
  HistoryElementContent,
  HistoryElementContentType,
  HistoryResponse,
  StatusType
} from './history.model';
import { MatDivider } from '@angular/material/divider';
import {
  getStatusMappingResult,
  StatusMappingResult,
  toHistoryElement,
  toJsonHistoryElementContent
} from './history.mapper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'pvmo-history',
  imports: [AsyncPipe, LoaderComponent, CardContainerComponent, MatDivider, BadgeComponent, TranslatePipe],
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HistoryComponent implements OnInit {
  private readonly historyService = inject(HistoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activatedRoute = inject(ActivatedRoute);

  id = this.activatedRoute.parent?.snapshot.paramMap.get('id');
  history$: Observable<HistoryResponse[]> | undefined;
  historyElementList: HistoryElement[] = [];
  parseError = false;

  ngOnInit(): void {
    if (this.id) {
      this.history$ = this.historyService.fetchHistoryContractByNumAuto(this.id).pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(response => !!response),
        catchError(err => {
          console.error(err);
          return EMPTY;
        }),
        tap(response => {
          for (const historyResponse of response) {
            const index = response.indexOf(historyResponse);
            const { content, contentType } = this.calculateHistoryElementContent(
              historyResponse.subStatus,
              historyResponse.content,
              index < response.length - 1 ? response[index + 1].subStatus : undefined
            );

            this.historyElementList.push(toHistoryElement(historyResponse, contentType, content));
          }
        })
      );
    }
  }

  statusInfo: Record<StatusType, { color: EnumBadgeColor; translation: string; displayChip: boolean }> = {
    [StatusType.DECLINED]: { color: EnumBadgeColor.ERROR, translation: 'declined', displayChip: true },
    [StatusType.UNDER_REVIEW]: { color: EnumBadgeColor.WARNING, translation: 'under-review', displayChip: false },
    [StatusType.FINAL]: { color: EnumBadgeColor.SUCCESS, translation: 'final', displayChip: true },
    [StatusType.CONDITIONAL_APPROVE]: {
      color: EnumBadgeColor.DEFAULT,
      translation: 'conditional-approve',
      displayChip: true
    },
    [StatusType.FUNDED]: { color: EnumBadgeColor.SUCCESS, translation: 'funded', displayChip: true },
    [StatusType.CLOSED]: { color: EnumBadgeColor.NEUTRAL, translation: 'closed', displayChip: true },
    [StatusType.ERROR]: { color: EnumBadgeColor.NEUTRAL, translation: 'error', displayChip: false },
    [StatusType.TO_CANCEL]: { color: EnumBadgeColor.WARNING, translation: 'to-cancel', displayChip: true },
    [StatusType.NOT_FUNDABLE]: { color: EnumBadgeColor.ERROR, translation: 'not-fundable', displayChip: true },
    [StatusType.NONE]: { color: EnumBadgeColor.NEUTRAL, translation: '', displayChip: false }
  };

  calculateHistoryElementContent(
    currentSubStatus: string,
    rawContent?: string,
    previousSubStatus?: string
  ): { content: HistoryElementContent[] | undefined; contentType: HistoryElementContentType | null } {
    if (rawContent) {
      if (rawContent?.includes('@JSON')) {
        const jsonStr = rawContent.replace(/^@JSON/, '').replaceAll(String.raw`\'`, "'");
        try {
          return {
            content: toJsonHistoryElementContent(JSON.parse(jsonStr)),
            contentType: HistoryElementContentType.JSON
          };
        } catch {
          this.parseError = true;
          return { content: undefined, contentType: null };
        }
      } else {
        return { content: [{ strContent: rawContent }], contentType: HistoryElementContentType.STRING };
      }
    } else if (previousSubStatus && currentSubStatus !== previousSubStatus) {
      const currentStatusType: StatusMappingResult = this.getStatusType(currentSubStatus);
      const previousStatusType: StatusMappingResult = this.getStatusType(previousSubStatus);

      if (currentStatusType.statusType === previousStatusType.statusType) {
        return { content: undefined, contentType: null };
      } else {
        return {
          content: [
            {
              statusChange: {
                oldStatus: previousStatusType.statusType,
                newStatus: currentStatusType.statusType,
                displayChip: currentStatusType.displayChip
              }
            }
          ],
          contentType: HistoryElementContentType.STATUS_CHANGE
        };
      }
    } else return { content: undefined, contentType: null };
  }

  getStatusType(subStatus: string): StatusMappingResult {
    return getStatusMappingResult(subStatus);
  }

  protected readonly historyElementContentType = HistoryElementContentType;
}
